<template>
  <ValidationObserver ref="form" v-slot="{ handleSubmit, invalid, pristine }" slim>
    <form
      role="form"
      class="form-horizontal"
      @submit.prevent="handleSubmit(onSubmit)"
    >
      <fieldset>
        <!-- id -->
        <Field
          v-if="currentBuyer.id"
          :label="$t('BUYER_ID')"
          :has-tooltip="false"
        >
          <template #element>
            <div class="col-sm-6 col-md-4 p-t-2 p-b-10">
              <p>
                <div
                  v-clipboard="{ copyText: currentBuyer.id }"
                  v-tooltip="'Copy'"
                  placement="right"
                  class="btn btn-link m-l-5 p-0 no_border"
                >
                  <i class="typcn typcn-tabs-outline" />
                </div>
                {{ currentBuyer.id }}
              </p>
            </div>
          </template>
        </Field>

        <!-- name -->
        <Field
          :label="$t('BUYER_COMPANY')"
          :has-tooltip="true"
          :tooltip-text="$t('BUYER_COMPANY_TT')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="name"
              rules="required"
              slim
            >
              <div :class="classes">
                <div class="col-xs-6 col-sm-4 col-md-4">
                  <input
                    v-model="currentBuyer.name"
                    :placeholder="$t('BUYER_COMPANY_DEFAULT')"
                    type="text"
                    name="name"
                    autocomplete="off"
                    class="form-control"
                    custom-autofocus="true"
                  />
                </div>
                <label
                  v-t="'REQUIRED'"
                  class="col-sm-3 col-md-3 col-xs-2 form-group__label-required"
                />
              </div>
            </ValidationProvider>
          </template>
        </Field>

        <!-- subId -->
        <Field
          :label="$t('BUYER_SUBID')"
          :has-tooltip="true"
          :tooltip-text="$t('BUYER_SUBID_TT')"
        >
          <template #element>
            <ValidationProvider v-slot="{ classes }" ref="subId" slim>
              <div :class="classes">
                <div class="col-xs-6 col-sm-4 col-md-4">
                  <input
                    v-model="currentBuyer.subId"
                    type="text"
                    :placeholder="$t('BUYER_SUBID_DEFAULT')"
                    name="subId"
                    autocomplete="off"
                    class="form-control"
                  />
                </div>
              </div>
            </ValidationProvider>
          </template>
        </Field>

        <!-- canPauseTargetsn -->
        <Field
          :label="$t('BUYER_ALLOW_PAUSE_TARGET')"
          :has-tooltip="true"
          :tooltip-text="$t('BUYER_ALLOW_PAUSE_TARGET_TT')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="canPauseTargets"
              rules=""
              slim
            >
              <div :class="classes">
                <div class="col-md-8 col-sm-8">
                  <NzToggle
                    v-model="currentBuyer.canPauseTargets"
                    name="canPauseTargets"
                  />
                </div>
              </div>
            </ValidationProvider>
          </template>
        </Field>

        <!-- canSetConcurrencyCaps -->
        <Field
          :label="$t('BUYER_ALLOW_SET_TARGET_CONCURRENCY')"
          :has-tooltip="true"
          :tooltip-text="$t('BUYER_ALLOW_SET_TARGET_CONCURRENCY_TT')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="canSetConcurrencyCaps"
              rules=""
              slim
            >
              <div :class="classes">
                <div class="col-md-8 col-sm-8">
                  <NzToggle
                    v-model="currentBuyer.canSetConcurrencyCaps"
                    name="canSetConcurrencyCaps"
                  />
                </div>
              </div>
            </ValidationProvider>
          </template>
        </Field>

        <!-- canDisputeConversions -->
        <Field
          :label="$t('BUYER_ALLOW_DISPUTE_CONVERSION')"
          :has-tooltip="true"
          :tooltip-text="$t('BUYER_ALLOW_DISPUTE_CONVERSION_TT')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="canDisputeConversions"
              rules=""
              slim
            >
              <div :class="classes">
                <div class="col-md-8 col-sm-8">
                  <NzToggle
                    v-model="currentBuyer.canDisputeConversions"
                    name="canDisputeConversions"
                  />
                </div>
              </div>
            </ValidationProvider>
          </template>
        </Field>

        <CapSetting
          :cap-settings-type="capSettingType"
          :call-caps="callCaps"
          :settings-object-container="currentBuyer.capacity"
          :translation-dictionary="buyerCapFieldsTranslation"
          :enable-caps-label="$t('BUYER_HAS_CAPS')"
          :enable-caps-tooltip="$t('BUYER_HAS_CAPS_TT')"
          :readonly="false"
          @change-call-caps="changeCallCaps"
          @change-settings-object-container="changeSettingsObjectContainer"
          @remove-caps="removeCaps()"
        />

        <RevenueRecovery
          v-if="hasRevenueRecovery"
          :toggle-options="revenueRecoverySettingsOptions"
          :setting="currentBuyer.hangupRerouteSetting"
          @change="updateRevenueRecoverySetting"
          :tooltip-text="$t('DIAL_SETTINGS_REVENUE_RECOVERY_BUYER_TT')"
        />

        <BuyerRouteDuplicateSetting
          :setting="currentBuyer.routeDuplicates"
          @change="onDuplicateSettingChange($event)"
        />

        <div v-if="hasRpc">
          <legend class="col-lg-offset-1 col-lg-10">
            <span>{{ $t('RPC_SETTING_TITLE') }}</span>
            <a
              v-tooltip.hover.right="$t('RPC_SETTING_TITLE_TT')"
              class="m-l-10 f-s-16"
            >
              <i class="fa fa-question-circle"></i>
            </a>
          </legend>

          <Field
            :label="$t('ERPC')"
            :has-tooltip="true"
            :tooltip-text="$t('USE_ERPC_TT')"
            class="m-b-5"
          >
            <template #element>
              <ValidationProvider
                v-slot="{ classes }"
                ref="rpcSetting"
                rules=""
                slim
              >
                <div :class="classes">
                  <div class="ol-sm-9 col-md-9">
                    <MultipleToggle
                      :options="rpcSettingsOptions"
                      :value="overrideRpcSetting"
                      @onToggle="onRPCSettingToggled"
                    />
                  </div>
                </div>
              </ValidationProvider>
            </template>
          </Field>

          <RPCSettings
            :rpc-setting="currentBuyer.rpcSetting"
            :rpc="rpcItem"
            :priority-bump="currentBuyer.priorityBump"
            entity-type="buyer"
            :show-setting-section-header="false"
            @rpc-settings-change="onRPCSettingsChange"
            @rpc-change="onRPCChange"
            @priorityBumpChange="onPriorityBumpChange"
          />
        </div>
        <ShareableTags
          :override-shareable-tags.sync="overrideShareableTags"
          :shareableTagSetting.sync="currentBuyer.shareableTagSetting"
          :available-tags="tags"
          :loading-tags="isLoadingTags"
        />

        <div class="form-group" data-testid="submit-wrapper">
          <div class="col-sm-8 col-md-8 col-md-offset-3 col-sm-offset-3">
            <p v-if="invalid && triedToSend" class="m-t-8 m-b-8 bright-red">
              <i class="f-s-20 p-r-0 typcn typcn-warning" />{{
                $t('INVALID_FORM')
              }}
            </p>
            <PrimaryButton
              size="sm"
              :show-loader-on-form="showLoader"
              data-testid="submit"
              @click="triedToSend = true"
            >
              <span v-t="currentBuyer.id ? 'UPDATE' : 'CREATE'" />
            </PrimaryButton>

            <button
              v-t="'CANCEL'"
              class="btn btn-sm btn-danger m-l-15"
              @click="cancel"
              data-testid="cancel"
            />
          </div>
        </div>
      </fieldset>
    </form>
  </ValidationObserver>
</template>

<script>
import Field from '@/core/components/LabelAndElement/LabelAndElement.vue';
import PrimaryButton from '@/core/components/PrimaryButton';
import TruncatedTextCopy from '@/angularBridgeCode/components/TruncatedTextCopy';
import NzToggle from '@/core/components/nzToggle/nzToggle';
import CapSetting from '@/core/components/CapSetting/CapSetting.vue';
import BuyerRouteDuplicateSetting from '@/modules/buyers/components/BuyerRouteDuplicateSetting.vue';
import MultipleToggle from '@/core/components/MultipleToggle/MultipleToggle.vue';
import RPCSettings from '@/core/components/RPCSettings/index.vue';
import ShareableTags from '@/core/components/ShareableTags/ShareableTags.vue';
import RevenueRecovery from '@/core/components/RevenueRecovery';

import messages from '../locales';
import sharedMessages from '@/core/locales';

import {
  capSettingsService,
  utilities,
} from '@/angularBridgeCode/ringbaPortal.servicesModule';

import { mapGetters, mapActions, mapState } from 'vuex';
import { GET_TAGS } from '@/modules/info/store/actionTypes';

export default {
  name: 'BuyerForm',
  components: {
    Field,
    PrimaryButton,
    TruncatedTextCopy,
    NzToggle,
    CapSetting,
    BuyerRouteDuplicateSetting,
    MultipleToggle,
    RPCSettings,
    ShareableTags,
    RevenueRecovery,
  },
  props: {
    buyer: {
      type: Object,
      default: () => ({}),
    },
    showLoader: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    overrideRpcSetting: false,
    rpcItem: null,
    callCaps: {},
    overrideShareableTags: false,
    currentBuyer: {
      canPauseTargets: false,
      canSetConcurrencyCaps: false,
      canDisputeConversions: false,
      priorityBump: '0',
    },
    triedToSend: false,
    isLoadingTags: false,
  }),
  computed: {
    ...mapState('info', ['tags']),
    ...mapGetters({
      hasRpc: 'hasRpc',
      hasRevenueRecovery: 'hasRevenueRecovery'
    }),
    ...mapGetters('buyersV2', ['createBuyerStatus', 'updateBuyerStatus']),
    capSettingType() {
      return capSettingsService.capSettingType.SUM;
    },
    buyerCapFieldsTranslation() {
      return capSettingsService.buyerCapFieldsTranslation;
    },
    rpcSettingsOptions() {
      return [
        { name: this.$t('USE_CAMPAIGN_SETTING'), value: false },
        { name: this.$t('USE_ERPC'), value: true },
      ];
    },
    revenueRecoverySettingsOptions() {
      return [
        { name: this.$t('USE_CAMPAIGN_SETTING'), value: null },
        { name: this.$t('ENABLED'), value: true },
        { name: this.$t('DISABLED'), value: false }
      ];
    }
  },
  methods: {
    ...mapActions({
      getTags: `info/${GET_TAGS}`,
    }),
    changeSettingsObjectContainer(value) {
      this.currentBuyer.capacity = { ...this.currentBuyer.capacity, ...value };
    },
    changeCallCaps(value) {
      this.callCaps = value;
    },
    onSubmit() {
      this.triedToSend = true;
      capSettingsService.resetCallCaps(
        this.capSettingType,
        this.callCaps,
        this.currentBuyer.capacity
      );

      // Trim whitespaces from the name field before save
      if (this.currentBuyer.name) {
        this.currentBuyer.name = this.currentBuyer.name.trim();
      }

      if (this.currentBuyer?.routeDuplicates?.trackBy === 'none') {
        this.currentBuyer.routeDuplicates = null;
      }

      if (this.overrideShareableTags) {
        this.processShareableTags(this.currentBuyer);
      } else {
        this.resetShareableTags(this.currentBuyer);
      }

      const userCreatedPredictiveRouting = this.rpcItem?.id;
      /**
       *  If there isn't an existing rpc object and a user created predictive
       *  routing config is selected, null out the rpcSetting object
       */
      if (!this.currentBuyer.rpc && userCreatedPredictiveRouting) {
        this.currentBuyer.rpcSetting = null;
      }
      /**
       * If migrated predictive routing config was enabled but a user created
       * predictive routing config is selected, null out the rpcSetting object
       */
      if (
        this.currentBuyer.rpc?.id &&
        userCreatedPredictiveRouting &&
        this.currentBuyer.rpc.id !== this.rpcItem.id
      ) {
        this.currentBuyer.rpcSetting = null;
      }
      /**
       * If user selects to use parent RPC settings, null out the rpcSetting
       * object
       */
      if (!this.overrideRpcSetting) {
        this.currentBuyer.rpcSetting = null;
      }

      const payload = { buyer: this.currentBuyer, rpcItem: this.rpcItem };
      if (!this.currentBuyer.id) {
        payload.buyer.doNotCreateUser = true;
      }
      this.$emit('submit', payload);
    },
    cancel(evt) {
      evt.preventDefault();
      this.$emit('cancel', !this.currentBuyer.id ? null : this.buyer);
    },
    onDuplicateSettingChange(duplicateSetting) {
      this.currentBuyer.routeDuplicates = duplicateSetting;
    },
    onSetRpcSetting(val) {
      this.currentBuyer.rpcSetting = val.value;
    },
    onRPCSettingToggled({ value }) {
      const isEnabled = value;

      if (isEnabled == 'campaignSetting') {
        // parent setting will take precedence
        this.currentBuyer.rpcSetting = null;
        return;
      }

      this.currentBuyer.rpcSetting = {
        erpcSetting: null,
        useERPC: isEnabled,
      };

      this.overrideRpcSetting = isEnabled;
      this.rpcItem = {
        enabled: isEnabled,
        id: '',
      };

    },
    onRPCSettingsChange(value) {
      this.currentBuyer.rpcSetting = value;
    },
    onRPCChange(value) {
      this.rpcItem.id = value;
    },
    onPriorityBumpChange(value) {
      this.currentBuyer.priorityBump = value;
    },
    initializeCallCaps() {
      this.currentBuyer.capacity = this.currentBuyer.capacity || {};
      this.callCaps = capSettingsService.initializeCallCaps(
        this.capSettingType,
        this.callCaps,
        this.currentBuyer.capacity
      );
      this.currentBuyer.capacity = capSettingsService.initializeCallCapSettings(
        this.capSettingType,
        this.currentBuyer.capacity
      );
    },
    removeCaps() {
      Object.keys(this.callCaps).forEach((key) => {
        this.callCaps[key] = -1;
      });
    },

    onRPCSettingsChange(value) {
      this.currentBuyer.rpcSetting = value;
    },
    setSanitizedRpcSetting() {
      if (this.currentBuyer.rpcSetting == null) {
        this.sanitizedRPCSetting = 'campaignSetting';
      } else {
        this.sanitizedRPCSetting = this.currentBuyer.rpcSetting.useERPC;
      }
    },
    async loadTags() {
      this.isLoadingTags = true;
      try {
        await this.getTags();
      } catch (e) {
        utilities.errorHandler(e);
      } finally {
        this.isLoadingTags = false;
      }
    },
    processShareableTags(buyer) {
      buyer.shareableTagSetting.shareInboundCallId =
        buyer.shareableTagSetting.shareInboundCallId || false;

      if (buyer.shareableTagSetting.macroTagstoShare) {
        buyer.shareableTagSetting.macroTagstoShare =
          buyer.shareableTagSetting.macroTagstoShare.map((tag) => {
            return tag.tagType + ':' + tag.tagName;
          });
      }
    },
    resetShareableTags(buyer) {
      buyer.shareableTagSetting = null;
    },
    initRpcSettingItem() {
      if (this.currentBuyer?.rpc && this.currentBuyer?.rpc?.id) {
        this.rpcItem = _.cloneDeep(this.currentBuyer.rpc);
        this.overrideRpcSetting = true;
        return;
      }
      this.rpcItem = {
        enabled: false,
        id: '',
      };
    },
    updateRevenueRecoverySetting(setting) {
      this.currentBuyer.hangupRerouteSetting = setting;
    }
  },
  created() {
    if (this.buyer && this.buyer.id) {
      this.currentBuyer = _.cloneDeep(this.buyer);
      this.currentBuyer.priorityBump = this.currentBuyer.priorityBump?.toString() || '0';
    }

    this.initializeCallCaps();
    this.setSanitizedRpcSetting();
    this.initRpcSettingItem();
  },
  mounted() {
    this.loadTags();

    if (this.buyer?.shareableTagSetting?.macroTagstoShare) {
      this.overrideShareableTags = true;
      this.currentBuyer.shareableTagSetting = this.buyer.shareableTagSetting;
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en',
  },
};
</script>
